import Notify from 'quasar/src/plugins/Notify.js';;

/**
 * Icône par défaut selon la couleur passée à `Notify.create`.
 *
 * Variantes contourées : le glyphe dessine lui-même son cercle, comme la
 * maquette. Un glyphe plein aurait fait doublon avec un anneau CSS.
 */
const ICON_BY_COLOR: Record<string, string> = {
  positive: 'check_circle_outline',
  teal: 'check_circle_outline',
  green: 'check_circle_outline',
  negative: 'highlight_off',
  red: 'highlight_off',
  warning: 'warning_amber',
  orange: 'warning_amber',
  info: 'info_outline',
  primary: 'info_outline',
  blue: 'info_outline',
};

/**
 * Dote chaque notification d'une icône correspondant à son type.
 *
 * La configuration Quasar (`config.notify`) ne connaît qu'une icône globale,
 * identique pour tous les types : impossible d'y exprimer « coche verte pour un
 * succès, croix rouge pour une erreur ». `Notify.registerType` n'aide pas
 * davantage, il s'indexe sur `type` alors que le code appelle `Notify.create`
 * avec `color`.
 *
 * On interpose donc une surcouche sur `Notify.create`, qui complète l'icône
 * quand l'appelant n'en fournit pas. C'est le seul moyen d'obtenir un rendu
 * homogène sans reprendre les quelque cent appels existants — et une icône
 * explicitement passée reste toujours prioritaire.
 *
 * À appeler une fois, après l'installation du plugin Quasar.
 */
export function applyNotifyTypeIcons(): void {
  const notify = Notify as any;

  // Idempotent : un second appel ne doit pas empiler les surcouches
  if (notify.__typeIconsApplied) {
    return;
  }

  const originalCreate = notify.create.bind(notify);

  notify.create = (options: any) => {
    // Forme courte `Notify.create('message')` : aucune couleur à interpréter
    if (!options || typeof options === 'string') {
      return originalCreate(options);
    }

    // Un visuel déjà fourni par l'appelant n'est jamais remplacé
    if (options.icon || options.avatar || options.spinner || options.html) {
      return originalCreate(options);
    }

    const icon = ICON_BY_COLOR[options.color];
    return originalCreate(icon ? { ...options, icon } : options);
  };

  notify.__typeIconsApplied = true;
}
